
import { safeLog } from '../utils/safeLog';

interface CircuitBreakerState {
  failures: number;
  lastFailure: number;
  lastSuccess: number;
  state: 'closed' | 'open' | 'half-open';
  totalRequests: number;
  successfulRequests: number;
}

export class AICircuitBreaker {
  private static states = new Map<string, CircuitBreakerState>();
  private static readonly FAILURE_THRESHOLD = 3;
  private static readonly TIMEOUT = 60000; // 1 minute
  private static readonly HALF_OPEN_MAX_CALLS = 3; // Allow 3 calls in half-open state
  private static halfOpenCalls = new Map<string, number>();
  
  static isProviderAvailable(provider: string): boolean {
    const state = this.states.get(provider);
    if (!state) return true;
    
    const now = Date.now();
    
    switch (state.state) {
      case 'closed':
        return true;
      case 'open':
        if (now - state.lastFailure > this.TIMEOUT) {
          safeLog.debug(`🔄 Circuit breaker for ${provider} transitioning to half-open`);
          state.state = 'half-open';
          this.halfOpenCalls.set(provider, 0);
          return true;
        }
        return false;
      case 'half-open':
        const callCount = this.halfOpenCalls.get(provider) || 0;
        return callCount < this.HALF_OPEN_MAX_CALLS;
      default:
        return true;
    }
  }
  
  static recordSuccess(provider: string): void {
    let state = this.states.get(provider);
    if (!state) {
      state = { 
        failures: 0, 
        lastFailure: 0, 
        lastSuccess: Date.now(),
        state: 'closed',
        totalRequests: 0,
        successfulRequests: 0
      };
      this.states.set(provider, state);
    }
    
    state.failures = 0;
    state.lastSuccess = Date.now();
    state.totalRequests++;
    state.successfulRequests++;
    
    if (state.state === 'half-open') {
      safeLog.debug(`✅ Circuit breaker for ${provider} closing after successful half-open test`);
      state.state = 'closed';
      this.halfOpenCalls.delete(provider);
    } else if (state.state === 'open') {
      state.state = 'closed';
    }
  }
  
  static recordFailure(provider: string): void {
    let state = this.states.get(provider);
    if (!state) {
      state = { 
        failures: 0, 
        lastFailure: 0,
        lastSuccess: 0,
        state: 'closed',
        totalRequests: 0,
        successfulRequests: 0
      };
      this.states.set(provider, state);
    }
    
    state.failures++;
    state.lastFailure = Date.now();
    state.totalRequests++;
    
    if (state.state === 'half-open') {
      safeLog.warn(`❌ Circuit breaker for ${provider} opening after half-open failure`);
      state.state = 'open';
      this.halfOpenCalls.delete(provider);
    } else if (state.failures >= this.FAILURE_THRESHOLD) {
      state.state = 'open';
      safeLog.warn(`⚠️ Circuit breaker opened for ${provider} after ${state.failures} failures`);
    }
    
    // Increment half-open call count if in that state
    if (state.state === 'half-open') {
      const currentCalls = this.halfOpenCalls.get(provider) || 0;
      this.halfOpenCalls.set(provider, currentCalls + 1);
    }
  }
  
  static getStats(): Array<{
    provider: string;
    state: string;
    failures: number;
    successRate: number;
    lastFailure: number;
    lastSuccess: number;
  }> {
    const stats: Array<{
      provider: string;
      state: string;
      failures: number;
      successRate: number;
      lastFailure: number;
      lastSuccess: number;
    }> = [];
    
    for (const [provider, state] of this.states) {
      const successRate = state.totalRequests > 0 
        ? (state.successfulRequests / state.totalRequests) * 100 
        : 0;
        
      stats.push({
        provider,
        state: state.state,
        failures: state.failures,
        successRate: Math.round(successRate * 100) / 100,
        lastFailure: state.lastFailure,
        lastSuccess: state.lastSuccess
      });
    }
    
    return stats;
  }
  
  static reset(provider?: string): void {
    if (provider) {
      safeLog.debug(`🔄 Resetting circuit breaker for ${provider}`);
      this.states.delete(provider);
      this.halfOpenCalls.delete(provider);
    } else {
      safeLog.debug('🔄 Resetting all circuit breakers');
      this.states.clear();
      this.halfOpenCalls.clear();
    }
  }
}
